import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
  Box,
} from "@material-ui/core";
import {
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
} from "@material-ui/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import firebase from "firebase";
import queryString from "query-string";

import GoogleSignIn from "./GoogleSignIn";
import FacebookSignIn from "./FacebookSignIn";
import AppleSignIn from "./AppleSignIn";
import { getCustomToken } from "../../../../services/accountApp/authService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { CustomTheme } from "../../../../types/customTheme";
import { AxiosError } from "axios";
import TextfieldDefault from "../../../../components/textField/TextfieldDefault";
import ValidationMessage from "../../../../components/validation/ValidationMessage";
import { handlePostMessage } from "./postMessage";

const useStyles = makeStyles((theme: CustomTheme) => ({
  root: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    width: "200%",
    height: 600, // 🔼 Deep curve
    borderBottomLeftRadius: "100%",
    borderBottomRightRadius: "100%",
    position: "absolute",
    top: -380, // 🔼 Shift it upward to center the curve
    left: "-50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingBottom: 24, // Optional space below logo
  },

  headerWrapper: {
    paddingTop: 220, // 🔼 Matches new curve depth
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  content: {
    flexGrow: 1,
    padding: "20px 20px",
  },
  inputField: {
    marginBottom: 16,
    borderRadius: 12,
  },
  loginButton: {
    height: 50,
    borderRadius: 12,
    marginTop: 8,
  },
  registerWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  bottomText: {
    textAlign: "center",
    fontSize: 12,
    marginTop: 20,
    padding: "0 20px",
  },
  logo: {
    width: "112px",
    minHeight: "112px",
    marginBottom: 8,
    objectFit: "cover",
    marginTop: "140px",
    borderRadius: "50%",
  },
}));

interface ManageAppsProps {
  handleChangeLoginStatus: () => void;
  handleOpenPasswordResetModal: () => void;
  isLoginOrRegister: boolean;
  setAuthError: (msg: string) => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC<ManageAppsProps> = ({
  handleChangeLoginStatus,
  handleOpenPasswordResetModal,
  isLoginOrRegister,
  setAuthError,
}) => {
  const classes = useStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState<any>();
  const [params, setParams] = useState<any>({
    headColour: "#f57c00",
    backColour: "#ffffff",
    buttonColour: "#000000",
    headText: "#ffffff",
    backText: "#000000",
    ButtonText: "#ffffff",
    title: "Sign In",
    subTitle: "",
    logo: "",
    privacyUrl: "Privacy policy, Terms & Conditions and Data Use Policy.",
  });

  const { register, errors, handleSubmit, trigger, setValue } =
    useForm<LoginFormData>();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleCreateCustomToken = async (idToken: string) => {
    try {
      const res: any = await getCustomToken({ idToken: idToken });
      if (res?.status === 200) {
        // const customToken = res.data.customToken;
        // handlePostMessage(customToken);
        const redirectUri = queryString.parse(
          window.location.search,
        ).redirectUri;
        const queryParams = `t=${res.data.customToken}`;
        setTimeout(() => {
          window.location.href = `${redirectUri}?${queryParams}`;
        }, 500);
      } else {
        setAuthError("error2 ERROR_MESSAGE_UNEXPECTED_ERROR");
      }
    } catch (error) {
      setAuthError("error1 ERROR_MESSAGE_UNEXPECTED_ERROR");
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setAuthError("");
    const isValid = await trigger();
    if (!isValid) return;

    setIsLoading(true);
    try {
      await firebase
        .auth()
        .signInWithEmailAndPassword(data.email.trim(), data.password.trim());

      const firebaseUser = firebase.auth().currentUser;
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken(true);
        await handleCreateCustomToken(idToken);
      }
    } catch (error) {
      const err: any = error as AxiosError;
      setAuthError("error3 err.message || ERROR_MESSAGE_UNEXPECTED_ERROR");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.search) {
      const parsed = queryString.parse(window.location.search);
      const privacyUrl = queryString.parse(window.location.search).privacyUrl;
      setPrivacyUrl(privacyUrl || "");
      const hasQueryParams = Object.keys(parsed).length > 0;
      if (!hasQueryParams) return;

      // Fix color values: prepend '#' if not already
      const colorKeys = [
        "headColour",
        "backColour",
        "buttonColour",
        "headText",
        "backText",
        "ButtonText",
        "title",
        "subTitle",
        "logo",
      ];

      const updatedParams: any = { ...parsed };

      colorKeys.forEach((key) => {
        if (updatedParams[key] && typeof updatedParams[key] === "string") {
          const val = updatedParams[key] as string;
          updatedParams[key] =
            key === "title" || key === "subTitle" || key === "logo"
              ? val
              : `#${val}`;
        }
      });

      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
    }
  }, []);
  // Extract colors/texts with fallbacks
  // const {
  //   headColour = "#f57c00",
  //   backColour = "#ffffff",
  //   buttonColour = "#000000",
  //   headText = "#ffffff",
  //   backText = "#000000",
  //   ButtonText = "#ffffff",
  //   title = "Sign In",
  //   subTitle = "",
  //   logo = "",
  // } = params;

  const handleRedirectPrivacyUrl = () => {
    window.open(privacyUrl, "_blank");
  };
  return (
    <div
      className={classes.root}
      style={{ backgroundColor: params.backColour }}
    >
      <Box className={classes.headerWrapper}>
        <Box
          className={classes.header}
          style={{ backgroundColor: params.headColour }}
        >
          {params.logo ? (
            <>
              <img src={params.logo} alt="Logo" className={classes.logo} />
              <Typography variant="body1" style={{ color: params.headText }}>
                Sign In
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                style={{ color: params.headText, fontWeight: "bold" }}
              >
                {params.title}
              </Typography>
              {params.subTitle && (
                <Typography
                  variant="subtitle1"
                  style={{ color: params.headText }}
                >
                  {params.subTitle}
                </Typography>
              )}
            </>
          )}
        </Box>

        <Box className={classes.content}>
          <form>
            <TextfieldDefault
              id="email"
              name="email"
              label="Email address"
              type="text"
              InputLabelProps={{
                style: {
                  color: params.backText, // <-- Set your label color here
                },
              }}
              style={{
                border: `1px solid ${params.buttonColour}`,
                backgroundColor: params.backColour,
              }}
              onChange={(e: any) => setValue("email", e.target.value)}
              inputRef={register({
                required: true,
                minLength: 2,
                maxLength: 80,
              })}
              inputProps={{
                style: {
                  color: params.backText, // <-- Typing text color
                },
              }}
              error={errors.email?.type}
            />
            {errors.email && (
              <ValidationMessage message="Please enter a valid email" />
            )}
            <TextfieldDefault
              id="password"
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              onChange={(e: any) => setValue("password", e.target.value)}
              inputRef={register({
                required: true,
                minLength: 2,
                maxLength: 80,
              })}
              InputLabelProps={{
                style: {
                  color: params.backText, // <-- Set your label color here
                },
              }}
              style={{
                border: `1px solid ${params.buttonColour}`,
                backgroundColor: params.backColour,
              }}
              inputProps={{
                style: {
                  color: params.backText, // <-- Typing text color
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {showPassword ? (
                        <VisibilityIcon style={{ color: params.backText }} />
                      ) : (
                        <VisibilityOffIcon style={{ color: params.backText }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={errors.password?.type}
            />
            {errors.password && (
              <ValidationMessage message="Please enter a valid password" />
            )}

            <Box style={{ textAlign: "left", marginBottom: 16 }}>
              <Button
                onClick={handleOpenPasswordResetModal}
                style={{
                  padding: 0,
                  color: params.backText,
                  textTransform: "none",
                }}
              >
                <Typography style={{ fontWeight: "bold" }}>
                  Forgot password?
                </Typography>
              </Button>
            </Box>

            <Button
              fullWidth
              className={classes.loginButton}
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
              style={{
                backgroundColor: params.buttonColour,
                color: params.ButtonText,
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "LOGIN"
              )}
            </Button>
          </form>

          <Box mt={4} mb={2} textAlign="center">
            <Typography style={{ color: params.backText }}>
              or Login in with
            </Typography>
            <Box display="flex" justifyContent="center" mt={2}>
              <GoogleSignIn
                backText={params.backText}
                backColour={"#FFFFFF"}
                handleCreateCustomToken={handleCreateCustomToken}
              />
              <AppleSignIn
                backText={params.backText}
                backColour={"#FFFFFF"}
                handleCreateCustomToken={handleCreateCustomToken}
              />
            </Box>
          </Box>

          <Box className={classes.registerWrapper}>
            <Typography style={{ color: params.backText }}>
              Don't have an account?
            </Typography>
            <Button
              onClick={handleChangeLoginStatus}
              style={{
                textTransform: "none",
                fontWeight: "bold",
                color: params.backText,
              }}
            >
              Register
            </Button>
          </Box>
 
          <Typography
            className={classes.bottomText}
            style={{ color: params.backText }}
          >
            By logging in to your account you're agreeing to our{" "}
            <span
              style={{
                fontWeight: "bold",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={handleRedirectPrivacyUrl}
            >
              Privacy policy, Terms & Conditions and Data Use Policy.
            </span>
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default LoginPage;
